import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, Plus, Upload } from 'lucide-react';
import { api } from '../../lib/api';

export function BankAccountsPage() {
  const queryClient = useQueryClient();
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    accountName: '',
    bankName: '',
    branchName: '',
    accountNumber: '',
    accountType: '普通',
  });

  const { data, isLoading } = useQuery({
    queryKey: ['bank-accounts'],
    queryFn: () => api.get<{ data: any[] }>('/api/bank/accounts'),
  });

  const addMutation = useMutation({
    mutationFn: (data: any) => api.post('/api/bank/accounts', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bank-accounts'] });
      setShowAddModal(false);
      setFormData({ accountName: '', bankName: '', branchName: '', accountNumber: '', accountType: '普通' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addMutation.mutate(formData);
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>
          銀行口座管理
        </h1>
        <div className="flex items-center gap-2">
          <button
            className="flex items-center gap-2 px-4 py-2 rounded-lg border text-sm"
            style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
          >
            <Upload size={16} />
            明細インポート
          </button>
          <button
            onClick={() => setShowAddModal(true)}
            className="flex items-center gap-2 px-4 py-2 rounded-lg text-white text-sm"
            style={{ backgroundColor: 'var(--accent-color)' }}
          >
            <Plus size={16} />
            口座追加
          </button>
        </div>
      </div>

      <div className="card overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-blue-600/30 border-t-blue-600 rounded-full animate-spin" />
          </div>
        ) : data?.data.length === 0 ? (
          <div className="text-center py-12">
            <Building2 size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <p style={{ color: 'var(--text-secondary)' }}>銀行口座が登録されていません</p>
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ color: 'var(--text-primary)' }}>口座名</th>
                <th style={{ color: 'var(--text-primary)' }}>銀行名</th>
                <th style={{ color: 'var(--text-primary)' }}>支店名</th>
                <th style={{ color: 'var(--text-primary)' }}>口座番号</th>
                <th style={{ color: 'var(--text-primary)' }}>種別</th>
                <th style={{ color: 'var(--text-primary)' }}>連携科目</th>
                <th style={{ color: 'var(--text-primary)' }}>状態</th>
              </tr>
            </thead>
            <tbody>
              {data?.data.map((account: any) => (
                <tr key={account.id}>
                  <td style={{ color: 'var(--text-primary)' }}>{account.accountName}</td>
                  <td style={{ color: 'var(--text-primary)' }}>{account.bankName}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{account.branchName || '-'}</td>
                  <td className="font-mono" style={{ color: 'var(--text-secondary)' }}>{account.accountNumber || '-'}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>{account.accountType || '-'}</td>
                  <td style={{ color: 'var(--text-secondary)' }}>
                    {account.linkedAccountCode ? `${account.linkedAccountCode} ${account.linkedAccountName}` : '-'}
                  </td>
                  <td>
                    <span className={`badge ${account.isActive ? 'badge-success' : 'badge-error'}`}>
                      {account.isActive ? '有効' : '無効'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* 口座追加モーダル */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="card p-6 w-full max-w-md animate-slideIn">
            <h2 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>銀行口座追加</h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>口座名 *</label>
                <input
                  type="text"
                  value={formData.accountName}
                  onChange={(e) => setFormData({ ...formData, accountName: e.target.value })}
                  required
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                  placeholder="メイン口座"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>銀行名 *</label>
                <input
                  type="text"
                  value={formData.bankName}
                  onChange={(e) => setFormData({ ...formData, bankName: e.target.value })}
                  required
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                  placeholder="三菱UFJ銀行"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>支店名</label>
                  <input
                    type="text"
                    value={formData.branchName}
                    onChange={(e) => setFormData({ ...formData, branchName: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border text-sm"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                    placeholder="新宿支店"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>口座番号</label>
                  <input
                    type="text"
                    value={formData.accountNumber}
                    onChange={(e) => setFormData({ ...formData, accountNumber: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border text-sm"
                    style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                    placeholder="1234567"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>口座種別</label>
                <select
                  value={formData.accountType}
                  onChange={(e) => setFormData({ ...formData, accountType: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg border text-sm"
                  style={{ backgroundColor: 'var(--input-bg)', borderColor: 'var(--input-border)', color: 'var(--text-primary)' }}
                >
                  <option value="普通">普通</option>
                  <option value="当座">当座</option>
                  <option value="定期">定期</option>
                </select>
              </div>
              <div className="flex justify-end gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddModal(false)}
                  className="px-4 py-2 rounded-lg border text-sm"
                  style={{ borderColor: 'var(--border-color)', color: 'var(--text-primary)' }}
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={addMutation.isPending}
                  className="px-4 py-2 rounded-lg text-white text-sm disabled:opacity-50"
                  style={{ backgroundColor: 'var(--accent-color)' }}
                >
                  {addMutation.isPending ? '追加中...' : '追加'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

